import { createFileRoute, Link } from "@tanstack/react-router";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { ArrowRight } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";

const STATS = [
  { n: "82%", body: "of all internet traffic is video in 2026 (Cisco / Sandvine)." },
  { n: "2.1×", body: "higher conversion on landing pages that lead with video." },
  { n: "1200%", body: "more shares for video than text and images combined (Brightcove)." },
  { n: "65%", body: "of B2B buyers watch video before booking a sales call." },
];

const REASONS = [
  {
    title: "Trust compounds faster",
    body: "Faces, voices and motion build credibility in seconds — the kind of trust a static page needs paragraphs to earn.",
  },
  {
    title: "Distribution actually works",
    body: "Every major platform — Meta, LinkedIn, YouTube, TikTok — pushes video to the top of the feed. Static gets buried.",
  },
  {
    title: "One shoot, many channels",
    body: "A single production day produces hero, social cutdowns, recruitment clips and sales assets. The ROI is rarely just one film.",
  },
  {
    title: "Sales cycles shorten",
    body: "Customer story films and demo videos do the heavy lifting before the first call. Sales reps close warmer leads.",
  },
];

export const Route = createFileRoute("/en_/why-video")({
  head: () =>
    ({
      meta: [
        { title: "Why video — The case for video in 2026 | Fenny" },
        {
          name: "description",
          content:
            "The numbers behind why video should be your largest marketing channel in 2026. Reach, trust, distribution and sales impact.",
        },
        { property: "og:title", content: "Why video in 2026" },
        {
          property: "og:description",
          content:
            "Reach, trust, distribution and sales impact — the case for video as your number-one channel.",
        },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: "https://fenny.se/en/why-video" },
      ],
      links: [
        { rel: "canonical", href: "https://fenny.se/en/why-video" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/why-video", name: "Why video — The case for video in 2026 | Fenny", description: "The numbers behind why video should be your largest marketing channel in 2026. Reach, trust, distribution and sales impact."})) },
      ],
    }) as never,
  component: WhyVideoPage,
});

function WhyVideoPage() {
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_20%_10%,color-mix(in_oklab,var(--butter)_12%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">
            Why video
          </div>
          <KineticHeading
            text="If you only fund one channel in 2026, fund this one."
            className="max-w-[22ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={{ indices: [9], className: "text-mint italic font-serif font-normal" }}
          />
          <p className="mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            Video is no longer a tactic. It's where attention lives, how trust
            is built and the format every platform rewards. Here's the case —
            with numbers.
          </p>
        </div>
      </section>

      <Reveal>
        <section className="py-16 md:py-24">
          <div className="container-wide grid gap-6 md:grid-cols-4">
            {STATS.map((s) => (
              <div
                key={s.n}
                className="rounded-3xl border border-white/10 bg-surface p-8"
              >
                <div className="text-5xl font-semibold text-mint">{s.n}</div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="container-wide">
            <h2 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">
              Four reasons video outperforms everything else.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {REASONS.map((r) => (
                <div
                  key={r.title}
                  className="rounded-3xl border border-white/10 bg-surface p-8"
                >
                  <h3 className="text-xl font-semibold">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {r.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide text-center">
            <h2 className="mx-auto max-w-3xl text-balance text-4xl font-semibold leading-tight md:text-6xl">
              Let's turn the case into a plan.
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Magnetic strength={0.3}>
                <Link
                  to="/en/contact"
                  className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
                >
                  Book a strategy call
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Link
                to="/en/work"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                See our work
              </Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
